import type { RacquetSportId } from '../constants/racquetSports';
import type { BaseEntity } from './index';

export const PLAY_EVENT_FORMATS = ['social_rotate', 'round_robin', 'single_elimination'] as const;
export type PlayEventFormat = (typeof PLAY_EVENT_FORMATS)[number];

export const PLAY_EVENT_SIDES = ['singles', 'doubles', 'mixed'] as const;
export type PlayEventSide = (typeof PLAY_EVENT_SIDES)[number];

export const SOCIAL_PLAY_MODES = ['singles_only', 'doubles_only', 'prefer_doubles'] as const;
export type SocialPlayMode = (typeof SOCIAL_PLAY_MODES)[number];

export const ODD_PLAYER_POLICIES = [
  'sit_out',
  'fallback_singles',
  'american_singles',
  'fallback_doubles_sit_out',
] as const;
export type OddPlayerPolicy = (typeof ODD_PLAYER_POLICIES)[number];

export const PAIRING_PRIORITIES = [
  'mixed_gender',
  'skill_balance',
  'player_rating_balance',
  'age_balance',
  'minimize_repeat_partners',
] as const;
export type PairingPriority = (typeof PAIRING_PRIORITIES)[number];

export const PLAY_JOIN_POLICIES = [
  'open',
  'members_only',
  'students_only',
  'members_and_students',
] as const;
export type PlayJoinPolicy = (typeof PLAY_JOIN_POLICIES)[number];

/** Who may join by gender. Exact match only when restricted; `other` is excluded. */
export const PLAY_GENDER_RESTRICTIONS = ['open', 'male', 'female'] as const;
export type PlayGenderRestriction = (typeof PLAY_GENDER_RESTRICTIONS)[number];

export const PLAY_PAYMENT_MODES = ['online_on_join', 'at_door'] as const;
export type PlayPaymentMode = (typeof PLAY_PAYMENT_MODES)[number];

export const PLAY_EVENT_STATUSES = [
  'draft',
  'published',
  'check_in_open',
  'in_progress',
  'completed',
  'cancelled',
] as const;
export type PlayEventStatus = (typeof PLAY_EVENT_STATUSES)[number];

/** Statuses shown on public browse/join surfaces. */
export const PUBLIC_JOINABLE_PLAY_EVENT_STATUSES = [
  'published',
  'check_in_open',
  'in_progress',
] as const satisfies readonly PlayEventStatus[];

export const PLAY_PARTICIPANT_STATUSES = [
  'registered',
  'checked_in',
  'withdrawn',
  'no_show',
] as const;
export type PlayParticipantStatus = (typeof PLAY_PARTICIPANT_STATUSES)[number];

export const PLAY_PAYMENT_STATUSES = ['unpaid', 'paid', 'comped', 'waived'] as const;
export type PlayPaymentStatus = (typeof PLAY_PAYMENT_STATUSES)[number];

export const PLAY_PARTICIPANT_SOURCES = ['staff', 'self', 'kiosk'] as const;
export type PlayParticipantSource = (typeof PLAY_PARTICIPANT_SOURCES)[number];

export const PLAY_PARTICIPANT_TYPES = ['member', 'student', 'guest'] as const;
export type PlayParticipantType = (typeof PLAY_PARTICIPANT_TYPES)[number];

export const PLAY_TEAM_ORIGINS = ['preformed', 'generated'] as const;
export type PlayTeamOrigin = (typeof PLAY_TEAM_ORIGINS)[number];

export const PLAY_ROUND_STATUSES = ['pending', 'in_progress', 'completed'] as const;
export type PlayRoundStatus = (typeof PLAY_ROUND_STATUSES)[number];

export const PLAY_MATCH_RESULTS = [
  'unplayed',
  'winner',
  'walkover',
  'retired',
  'bye',
  'cancelled',
] as const;
export type PlayMatchResult = (typeof PLAY_MATCH_RESULTS)[number];

export const PLAY_EVENT_BENEFITS = ['none', 'full_coverage', 'discount_percent'] as const;
export type PlayEventBenefit = (typeof PLAY_EVENT_BENEFITS)[number];

export type PlayParticipantGender = 'male' | 'female' | 'other';

export type PlaySeriesGenerationSkipReason = 'court_conflict' | 'duplicate';

export interface PlaySeriesGenerationSkippedOccurrence {
  startsAt: string;
  reason: PlaySeriesGenerationSkipReason;
}

export interface PlaySeriesGenerationSummary {
  createdCount: number;
  skipped: PlaySeriesGenerationSkippedOccurrence[];
  membersAutoEnrolled?: number;
  membersAutoEnrollmentSkipped?: number;
  studentsAutoEnrolled?: number;
  studentsAutoEnrollmentSkipped?: number;
}

export const PLAY_SERIES_SCHEDULE_PERIOD_OPTIONS = [3, 6, 12] as const;
export type PlaySeriesSchedulePeriodMonths = (typeof PLAY_SERIES_SCHEDULE_PERIOD_OPTIONS)[number];

export interface PlayEventSeries extends BaseEntity {
  organizationId: string;
  clubId: string;
  academyId?: string | null;
  name: string;
  sportId: RacquetSportId;
  academySportId?: string | null;
  format: PlayEventFormat;
  side: PlayEventSide;
  socialPlayMode?: SocialPlayMode | null;
  oddPlayerPolicy?: OddPlayerPolicy | null;
  pairingPriorities: PairingPriority[];
  joinPolicy: PlayJoinPolicy;
  paymentMode: PlayPaymentMode;
  genderRestriction: PlayGenderRestriction;
  memberPriceCents?: number | null;
  studentPriceCents?: number | null;
  guestPriceCents?: number | null;
  roundCount?: number | null;
  roundDurationMinutes?: number | null;
  roundBreakMinutes?: number | null;
  matchupVisibilityMinutes: number;
  nextMatchWithdrawMinutes: number;
  withdrawHoursBeforeStart?: number | null;
  defaultCourtIds: string[];
  defaultLocationId?: string | null;
  weekday?: number | null;
  defaultStartTime?: string | null;
  defaultDurationMinutes?: number | null;
  timezone: string;
  isActive: boolean;
  /** @deprecated Use kioskRegistrationEnabled — implicit roster auto-add is no longer exposed in UI. */
  autoAddActiveMembers: boolean;
  /** @deprecated Use kioskRegistrationEnabled — implicit roster auto-add is no longer exposed in UI. */
  autoAddActiveStudents: boolean;
  /** When true, members/students may register on the day via the event kiosk. */
  kioskRegistrationEnabled: boolean;
  /** When true, guests may register on the day via the event kiosk (requires kioskRegistrationEnabled). */
  kioskGuestRegistrationEnabled: boolean;
  /** When true, the series is promoted on the org public home page. */
  isFeatured: boolean;
  /** When true, operators can record match results and set scores in the draw UI. */
  matchScoringEnabled: boolean;
  /** Hard end of the current generation window; extend to add another period. */
  scheduleEndsAt?: Date | null;
  /** Length of each generation window in months (3, 6, or 12). Used for create and extend. */
  schedulePeriodMonths: PlaySeriesSchedulePeriodMonths;
  /** When operators were last notified that scheduleEndsAt is approaching. */
  scheduleEndReminderSentAt?: Date | null;
  lastGeneratedAt?: Date | null;
  lastGenerationSummary?: PlaySeriesGenerationSummary | null;
}

export interface PlayEvent extends BaseEntity {
  organizationId: string;
  clubId: string;
  academyId?: string | null;
  seriesId?: string | null;
  name: string;
  slug: string;
  sportId: RacquetSportId;
  academySportId?: string | null;
  format: PlayEventFormat;
  side: PlayEventSide;
  socialPlayMode?: SocialPlayMode | null;
  oddPlayerPolicy?: OddPlayerPolicy | null;
  pairingPriorities: PairingPriority[];
  joinPolicy: PlayJoinPolicy;
  paymentMode: PlayPaymentMode;
  genderRestriction: PlayGenderRestriction;
  memberPriceCents?: number | null;
  studentPriceCents?: number | null;
  guestPriceCents?: number | null;
  status: PlayEventStatus;
  startsAt: Date;
  endsAt: Date;
  locationId?: string | null;
  courtIds: string[];
  roundCount?: number | null;
  roundDurationMinutes?: number | null;
  roundBreakMinutes?: number | null;
  matchupVisibilityMinutes: number;
  nextMatchWithdrawMinutes: number;
  withdrawDeadlineAt?: Date | null;
  withdrawHoursBeforeStart?: number | null;
  capacity?: number | null;
  timezone: string;
  publishedAt?: Date | null;
  /** When true, the event is promoted on the org public home page. */
  isFeatured: boolean;
  /** When true, members/students may register on the day via the event kiosk. */
  kioskRegistrationEnabled: boolean;
  /** When true, guests may register on the day via the event kiosk (requires kioskRegistrationEnabled). */
  kioskGuestRegistrationEnabled: boolean;
  /** When true, operators can record match results and set scores in the draw UI. */
  matchScoringEnabled: boolean;
  /** Optional cover image URL for public play event cards and detail. */
  coverImageUrl?: string | null;
}

export interface PlayEventParticipant extends BaseEntity {
  playEventId: string;
  participantType: PlayParticipantType;
  memberId?: string | null;
  studentId?: string | null;
  guestFirstName?: string | null;
  guestLastName?: string | null;
  /** Resolved from linked member/student on list reads (not persisted on participant row). */
  displayFirstName?: string | null;
  displayLastName?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  gender?: PlayParticipantGender | null;
  skillLevel?: string | null;
  /** Snapshot of internal player rating at join time (operator-only). */
  playerRating?: number | null;
  dateOfBirth?: Date | null;
  status: PlayParticipantStatus;
  paymentStatus: PlayPaymentStatus;
  source: PlayParticipantSource;
  seed?: number | null;
  stripePaymentIntentId?: string | null;
  checkedInAt?: Date | null;
  /** When true, prioritize this player for fallback singles in prefer-doubles socials. */
  prefersSingles?: boolean;
  /** Resolved from linked member/student platform user on list reads (not persisted on participant row). */
  avatarUrl?: string | null;
  /** Series auto-add roster entry not yet persisted (resolved at read time). */
  isImplicit?: boolean;
}

export interface PlayEventRoundWithdrawal extends BaseEntity {
  participantId: string;
  playEventId: string;
  roundNumber: number;
}

export interface PlayEventTeam extends BaseEntity {
  playEventId: string;
  participantAId: string;
  participantBId: string;
  origin: PlayTeamOrigin;
  roundNumber?: number | null;
}

export interface PlayEventRound extends BaseEntity {
  playEventId: string;
  roundNumber: number;
  status: PlayRoundStatus;
  startsAt?: Date | null;
}

export interface PlayMatchScore {
  sets?: Array<{ a: number; b: number }>;
  games?: Array<{ a: number; b: number }>;
  winnerSide?: 'a' | 'b';
}

export interface PlayEventMatch extends BaseEntity {
  playEventId: string;
  roundId: string;
  roundNumber: number;
  /** 0-based position within the round bracket (stable draw order). */
  matchIndex?: number;
  courtId?: string | null;
  scheduledStart?: Date | null;
  sideAParticipantId?: string | null;
  sideBParticipantId?: string | null;
  sideATeamId?: string | null;
  sideBTeamId?: string | null;
  result: PlayMatchResult;
  winnerSide?: 'a' | 'b' | null;
  score?: PlayMatchScore | null;
  completedAt?: Date | null;
}
